import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { getPollMs } from '../utils/appSettings';
import { useLayoutImage } from "../context/LayoutContext";
import axios from "axios";
import config from "../config";
import "./Service.css";
import AssignMeats from "./AssignMeats";
import ActionsList from "./ActionsList";
import { renderSeatDots, getReservedCount } from './shared/renderSeatDots';
import TableConnections from './shared/TableConnections';
import WalkedInSitting from "./WalkedInSitting";
import { createPortal } from 'react-dom';
import { useBpFlags } from "../Responsive";
import TableSelector from "./TableSelector";
import { bbqTerm } from '../utils/bbqTerms';

import i18n from "../i18n";
const tr = i18n.t.bind(i18n);
// define once, at module scope
function Portal({ children }) {
  return typeof document !== 'undefined'
    ? createPortal(children, document.body)
    : null;
}

function Service() {

  const backgroundImage = useLayoutImage();
  const [tables, setTables] = useState([]);
  const [states, setStates] = useState([]);
  const [modalTable, setModalTable] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedTables, setSelectedTables] = useState([]);
  const [numPeople, setNumPeople] = useState(2);
  const [reservations, setReservations] = useState([]);
  const [activeTab, setActiveTab] = useState("current");
  const [comment, setComment] = useState("");
  const [order, setOrder] = useState(0);
  const [reservationDetails, setReservationDetails] = useState({});
  const [actions, setActions] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [originalState, setOriginalState] = useState(null);
  const [areasList, setAreasList] = useState([]);
  // Track baselines to know if something changed
  const [baselinePeople, setBaselinePeople] = useState(2);
  const [baselineOrder, setBaselineOrder] = useState(1);
  const [saveTick, setSaveTick] = useState(false); // show ✓ for 1s after save
  const prevActionsRef = useRef([]);
  const { isTablet, isMobile } = useBpFlags();
  const [tabletTab, setTabletTab] = useState('tables'); // 'tables' | 'events'

  // design-space for your absolute coords
  const DESIGN_W = 800;
  const DESIGN_H = 800;

  // measure container width and scale the 800×800 canvas to fit
  const resizerRef = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const node = resizerRef.current;
    if (!node) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || node.clientWidth || DESIGN_W;
      setScale(Math.max(0.2, w / DESIGN_W));
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const onToolbarRefresh = useCallback(() => setRefresh(r => !r), []);

  // keep this near the top of Service()
  const changeTablesTime = React.useMemo(() => {
    const src = modalTable?.reservation?.arrival || '';
    return src.replace(' ', 'T').slice(0, 16); // YYYY-MM-DDTHH:mm
  }, [modalTable?.reserved_id]);

  // Map states by numeric id to avoid '1' vs 1 mismatches
  const stateById = useMemo(() => {
    const m = new Map();
    (states || []).forEach(s => m.set(Number(s.id), s));
    return m;
  }, [states]);
  const getState = (id) => stateById.get(Number(id));

  // Keep a stable, ordered list of states (by numeric id)
  const orderedStates = useMemo(
    () => (states || []).slice().sort((a, b) => Number(a.id) - Number(b.id)),
    [states]
  );
  // Compute the "next" state by index in orderedStates (wrap around)
  const nextState = useMemo(() => {
    if (!orderedStates.length) return null;
    const idx = orderedStates.findIndex(s => Number(s.id) === Number(selectedState));
    const nextIdx = idx >= 0 ? (idx + 1) % orderedStates.length : 0;
    return orderedStates[nextIdx] || null;
  }, [orderedStates, selectedState]);

  const hasTables = (r) => !!(r?.tables && String(r.tables).trim());
  
  const BUZZER_TYPE_ID = 12;
  const BUZZER_SHAKE_MAX_SEC = 10 * 60; // 10 minutes
  
  const buzzerReservationIds = useMemo(() => {
    const set = new Set();
  
    (actions || []).forEach((a) => {
      const isBuzzer = Number(a?.action_type_id) === BUZZER_TYPE_ID;
      const isOpen = !a?.completed; // completed is null when still open
      const elapsed = Number(a?.elapsed ?? 0);
  
      if (isBuzzer && isOpen && elapsed < BUZZER_SHAKE_MAX_SEC && a?.reservation_id != null) {
        set.add(Number(a.reservation_id));
      }
    });
  
    return set;
  }, [actions]);


  useEffect(() => {
    axios.get(tr('{{API_BASE_URL}}/areas', {
      API_BASE_URL: config.API_BASE_URL
    }), { withCredentials: true })
      .then(r => setAreasList(r.data || []))
      .catch(() => setAreasList([]));
  }, []);

  // When any action flips from incomplete -> complete, refetch tables/states to refresh colors
  useEffect(() => {
    const prev = prevActionsRef.current || [];
    if (!Array.isArray(actions) || actions.length === 0) {
      prevActionsRef.current = actions;
      return;
    }
    // index previous actions by id -> completed
    const prevCompletedMap = new Map(prev.map(a => [a.id, !!a.completed]));
    // detect newly completed ones
    const newlyCompleted = actions.filter(a => !!a.completed && prevCompletedMap.get(a.id) === false);
    if (newlyCompleted.length > 0) {
      // trigger the fetch effect that reloads tables + states (covers multi-table reservations)
      setRefresh(r => !r);
    }
    prevActionsRef.current = actions;
  }, [actions]);

  useEffect(() => {
    async function fetchData() {
      const ac = new AbortController();
      const { signal } = ac;
      // stop any previous run when deps change
      let cancelled = false;
      if (modalTable && activeTab === 'changeTables') return;
      try {
        const req = (url, opts={}) => axios.get(url, { withCredentials:true, signal, ...opts });
        const [tableResponse, stateResponse, reservationsResponse] = await Promise.all([
          req(tr('{{API_BASE_URL}}/tables/view', {
            API_BASE_URL: config.API_BASE_URL
          })),
          req(tr('{{API_BASE_URL}}/states', {
            API_BASE_URL: config.API_BASE_URL
          })),
          req(tr('{{API_BASE_URL}}/reservations', {
            API_BASE_URL: config.API_BASE_URL
          })),
        ]);
        setTables(tableResponse.data);
        setStates(stateResponse.data);
        
        // throttle enrichment: max 4 at a time
        const ids = reservationsResponse.data.map(r => r.id);
        const enrichedReservations = [];
        const chunk = 4;
        for (let i=0; i<ids.length; i+=chunk) {
          const slice = ids.slice(i, i+chunk);
          const rows = await Promise.all(slice.map(id => {
            return req(tr('{{API_BASE_URL}}/assignedtables/reservation/{{id}}', {
              API_BASE_URL: config.API_BASE_URL,
              id
            }))
              .then(({data}) => ({ id, numbers: data.map(t=>t.number).join(', ') }));
          }
          ));
          rows.forEach(({id, numbers}) => {
            const r = reservationsResponse.data.find(x => x.id === id);
            if (r) enrichedReservations.push({ ...r, tables: numbers });
          });
        }

        setReservations(enrichedReservations);
        
        if(modalTable)
          setSelectedState(tableResponse?.data?.find((t)=>{return t.id === modalTable.id})?.state_id || modalTable.state_id);
    
      } catch (error) {
        console.error("Error fetching data", error);
      }
      return () => { cancelled = true; ac.abort(); };
    }
    fetchData();
    //const interval = setInterval(fetchData, 5000); // Refresh every 5 seconds
    
  }, [refresh, modalTable, activeTab]);

  const handleTableClick = (table) => {
    if (!table?.reserved_id) return;
    table.reservation = reservations.find(r => r.id === table.reserved_id);
    setModalTable(table);
    setSelectedState(Number(table.state_id));
    setOriginalState(Number(table.state_id));
  };

  useEffect(() => {
    if (modalTable) {
      setNumPeople(modalTable.reservation?.people || 2);
      setOrder(modalTable.reservation?.order || 1);
      setComment(modalTable.reservation?.comment || "");
      setReservationDetails({
        ...modalTable.reservation,
        people: modalTable.reservation?.people || 2,
        comment: modalTable.reservation?.comment || "",
        order: modalTable.reservation?.order || 1,
      });
      // set baselines when modal opens
      setBaselinePeople(modalTable.reservation?.people || 2);
      setBaselineOrder(modalTable.reservation?.order || 1);
      setSaveTick(false);            
    }
  }, [modalTable]);

  // ---- helper: refresh the color/state of a single table locally
  const applyTableStateLocally = React.useCallback((tableId, stateId) => {
    setTables(prev => prev.map(t =>
      t.id === tableId ? { ...t, state_id: stateId } : t
    ));
  }, []);

  // is anything changed (people / order / state)?
  const isDirty = useMemo(() => {
    return (
      Number(numPeople) !== Number(baselinePeople) ||
      Number(order) !== Number(baselineOrder) ||
      Number(selectedState) !== Number(originalState)
    );
  }, [numPeople, order, selectedState, baselinePeople, baselineOrder, originalState]);

  const handleCreateWalkedInReservation = async (tables) => {
    try {
      await axios.post(tr('{{API_BASE_URL}}/reservations/walkedin', {
        API_BASE_URL: config.API_BASE_URL
      }), {
        people: numPeople,
        tables,
      });
      setModalTable(null);
      setRefresh(r => !r);
    } catch (error) {
      console.error("Error creating walked-in reservation", error);
    }
  };

  const handleSaveReservation = async () => {
    if(!modalTable || !selectedState || selectedState < 2) return;
    if (!modalTable.reserved_id){
      handleCreateWalkedInReservation([modalTable.id]);
      return;
    }
    try {
      await axios.put(tr('{{API_BASE_URL}}/reservations/service/{{reserved_id}}', {
        API_BASE_URL: config.API_BASE_URL,
        reserved_id: modalTable.reserved_id
      }), {
        ...reservationDetails,
        people: numPeople,
        comment: comment || null,
        order: order || 0
      });

      if(selectedState !== originalState)
        await axios.post(tr('{{API_BASE_URL}}/assignedstates/{{reserved_id}}', {
          API_BASE_URL: config.API_BASE_URL,
          reserved_id: modalTable.reserved_id
        }), {
          state: selectedState,
        });
        
      // On success: update baselines so button disables again and show ✓ for 1s
      setBaselinePeople(Number(numPeople));
      setBaselineOrder(Number(order));
      setOriginalState(Number(selectedState));
      applyTableStateLocally(modalTable.id, Number(selectedState));
      setSaveTick(true);
      setTimeout(() => setSaveTick(false), 1000);
    } catch (error) {
      console.error("Error updating reservation", error);
    }
  };

  const handleSaveAssignedTables = async () => {
    if (!modalTable || !modalTable.reserved_id || selectedTables.length === 0) return;
    try {
      await axios.post(tr('{{API_BASE_URL}}/assignedtables', {
        API_BASE_URL: config.API_BASE_URL
      }), {
        reservationId: modalTable.reserved_id,
        tables: selectedTables,
      });
      //alert("Assigned tables updated successfully!");
      setModalTable(null);
      setSelectedTables([]);
      setRefresh(r => !r);
    } catch (error) {
      console.error("Error saving assigned tables", error);
    }
  };

  useEffect(() => {
    let interval;
    const fetchActions = async () => {
      try {
        const response = await axios.get(tr('{{API_BASE_URL}}/actions', {
          API_BASE_URL: config.API_BASE_URL
        }));
        setActions(response.data);
      } catch (error) {
        console.error("Error fetching actions:", error);
      }
    };
 // pause polling while the modal is open (any tab)
 const pause = !!modalTable && (activeTab === 'meats' || activeTab === 'changeTables');
    if (!pause) {
      fetchActions();
      interval = setInterval(fetchActions, getPollMs('fast'));
    }
    return () => interval && clearInterval(interval);
  }, [modalTable, activeTab]);

  useEffect(() => {
    const anyOpen = !!modalTable;
    const body = document.body;
    if (anyOpen) body.classList.add('no-scroll');
    else body.classList.remove('no-scroll');
  }, [modalTable]);

  return (
    <div className="service-page">
      {isTablet && (
        <div className="service-toolbar">
          <div className="tab-container" role="tablist">
            <div
              className={`tab ${tabletTab === 'tables' ? 'active' : ''}`}
              role="tab"
              onClick={() => setTabletTab('tables')}
            >{tr('Tables States')}</div>
            <div
              className={`tab ${tabletTab === 'events' ? 'active' : ''}`}
              role="tab"
              onClick={() => setTabletTab('events')}
            >
              {actions.filter(a => !a.completed).length || 'No'}{' '}{tr('Service Events')}</div>
          </div>
          {/* right side of toolbar: WalkedInSitting buttons */}
          <div style={{float: 'right', marginRight: '60px'}}>
            <WalkedInSitting
              areasList={areasList}
              onRefresh={onToolbarRefresh}
            />
          </div>
        </div>
      )}
      <div className="service-container">  
        {(!isTablet || tabletTab === 'tables') && (
          <div className="left-panel">

            <div style={{display: 'flow-root'}}>
              {!isTablet && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      marginBottom: 8
                    }}
                  >
                    <h2 style={{ margin: 0 }}>{tr("Tables States")}</h2>
              
                    <div style={{ display: "inline-flex", alignItems: "center", marginRight: 40 }}>
                      <WalkedInSitting
                        areasList={areasList}
                        onRefresh={onToolbarRefresh}
                        toolbarVariant={isMobile ? "icons" : "buttons"}  // ? like ReservationsList
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div
              ref={resizerRef}
              className="layout-resizer"
              style={{ height: tr('{{var1}}px', {
                var1: DESIGN_H * scale
              }) }}
            >
              <div
                className="restaurant-layout"
                style={{
                  backgroundImage: tr('url({{backgroundImage}})', {
                    backgroundImage
                  }),
                  width: tr('{{DESIGN_W}}px', {
                    DESIGN_W
                  }),
                  height: tr('{{DESIGN_H}}px', {
                    DESIGN_H
                  }),
                  transform: tr('scale({{scale}})', {
                    scale
                  }),
                  transformOrigin: 'top left'
                }}
              >
                {/*
                  Compute once per render: how many seats from each reservation
                  should be painted on each of its tables.
                */}
                {/*
                  useMemo placed here so we can reference `tables` and `reservations`
                  right before mapping; hoist to top of component if you prefer.
                */}
                {(() => {

                  // build: seatsByReservation[reservationId][tableId] = count
                  const grouped = {};
                  tables.forEach(t => {
                    if (t?.reserved_id) (grouped[t.reserved_id] ||= []).push(t);
                  });
                  const map = {};
                  Object.entries(grouped).forEach(([resIdStr, tbls]) => {
                    const resId = Number(resIdStr);
                    const res = reservations.find(r => r.id === resId);
                    const people = Math.max(0, parseInt(res?.people ?? 0, 10) || 0);
                    const sorted = [...tbls].sort((a,b) => a.id - b.id);
                    const n = sorted.length || 1;
                    const base = Math.floor(people / n);
                    let rem = people % n;
                    const counts = {};
                    sorted.forEach(t => {
                      const cap = Math.max(0, parseInt(t.capacity ?? 0, 10) || 0);
                      let c = base + (rem > 0 ? 1 : 0);
                      if (rem > 0) rem--;
                      counts[t.id] = Math.max(0, Math.min(c, cap));
                    });
                    map[resId] = counts;
                  });
                  // stash on window for debugging if you want:
                  // window._seatsByReservation = map;
                  return (
                    <>
                      {tables.map(table => {
                        return (
                          <div
                            key={table.id}
                            className={[
                              'table-box',
                              (!table.reserved_id && Number(table.state_id) < 5) ? 'disabled' : '',
                              actions.some(a => Number(a.reservation_id) === Number(table.reserved_id) && !a.completed) ? 'flash-border' : '',
                              buzzerReservationIds.has(Number(table.reserved_id)) ? 'shake' : '',
                            ].filter(Boolean).join(' ')}
                            title={getState(table.state_id)?.name || ''}
                            style={{
                              backgroundColor: getState(table.state_id)?.color || 'gray',
                              top: tr('{{monitor_y}}px', {
                                monitor_y: table.monitor_y
                              }),
                              left: tr('{{monitor_x}}px', {
                                monitor_x: table.monitor_x
                              }),
                              width: tr('{{width}}px', {
                                width: table.width
                              }),
                              height: tr('{{height}}px', {
                                height: table.height
                              }),
                              border: tr('1px solid {{var1}}', {
                                var1: table.isAvailable ? 'black' : 'red'
                              }),
                              boxShadow: tr('{{var1}}', {
                                var1: table.isAvailable ? 'none' : '2px 1px 2px darkRed'
                              }),
                              cursor: table.reserved_id ? 'pointer' : 'not-allowed'
                            }}
                            onClick={() => { if (table.reserved_id) handleTableClick(table); }}
                          >
                            <b>{table.number}</b>
                            <span style={{ fontSize: '9px' }}>{table.nextReservation}</span>
                            {renderSeatDots(
                              table,
                              1,
                              0,
                              { reservedCount: getReservedCount(table, tables, reservations), reservedClass: 'reserved-seat-red' }
                            )}
                          </div>
                        );
                      })}
                      <TableConnections
                        tables={tables}
                        groupBy={(t) => t.reserved_id}
                        coord="monitor"
                        stroke="darkRed"
                        strokeWidth={2}
                      />
                    </>
                  );
                })()}        
              </div>
            </div>
          </div>
        )}
        {(!isTablet || tabletTab === 'events') && (
          <div className="right-panel" style={{zoom: (isMobile? '0.4' : '1') }}>
            {!isTablet && (<h2>{actions.filter(a => !a.completed).length || 'No'}{' '}{tr('Service Events')}</h2>)}
            {!actions.length ? (<p>{tr('..the last hour there are no more events to show..')}</p>)
            : (
              <div className="service-events-container">
                <ActionsList actions={actions} setRefresh={setRefresh} />
              </div>
            )}
          </div>
        )}
      </div>
      {modalTable && (
        <Portal>
          <div className="svc-modal-overlay" onClick={() => setModalTable(null)}>
            <div className="svc-modal" onClick={(e) => e.stopPropagation()}>
      
              {/* ? Mobile-friendly modal header */}
              <div className="svc-modal-head">
                <div className="svc-modal-title">
                  {tr('Table')} {modalTable.number}
                </div>
      
                <button
                  type="button"
                  className="svc-modal-close"
                  onClick={() => setModalTable(null)}
                  aria-label={tr('Close')}
                >
                  {"\u00D7"}
                </button>
              </div>
      
              {/* ? Tabs row (scrollable on mobile) */}
              <div className="svc-modal-tabs" role="tablist">
                <button
                  type="button"
                  className={`svc-tab ${activeTab === "current" ? "active" : ""}`}
                  onClick={() => setActiveTab("current")}
                >
                  {tr('Current State')}
                </button>
      
                <button
                  type="button"
                  className={`svc-tab ${activeTab === "meats" ? "active" : ""}`}
                  onClick={() => setActiveTab("meats")}
                >
                  {bbqTerm('Assigned Meats')}
                </button>
      
                <button
                  type="button"
                  className={`svc-tab ${activeTab === "changeTables" ? "active" : ""}`}
                  onClick={() => setActiveTab("changeTables")}
                >
                  {tr('Change Tables')}
                </button>
              </div>
      
              <div className="svc-modal-body">
                <div className="tab-content">
                  {activeTab === "current" && (
                    <>
                      <div className="svc-current-compact">
                        <div className="svc-current-v2">
                      
                          {/* Row 1: Reservation + Order */}
                          {modalTable.state_id > 1 && (
                            <div className="svcRow svcRowTop">
                              <div className="svcResId">
                                {tr("Reservation:")} <b>{modalTable.reserved_id}</b>
                              </div>
                      
                              {modalTable.state_id > 5 && (
                                <div className="svcOrderBox">
                                  <span className="svcOrderLbl">{tr("Order:")}</span>
                                  <input
                                    className="svcOrderInput"
                                    type="number"
                                    onChange={(e) => setOrder(e.target.value)}
                                    min="0"
                                    max="999"
                                    value={order}
                                  />
                                </div>
                              )}
                            </div>
                          )}
          
                          {/* Row: People (compact with - / +) */}
                          <div className="svcRow svcRowPeople">
                            <div className="svcPeopleLeft">
                              <span className="svcPeopleIcon" aria-hidden="true">{tr('👨')}</span>
                              <span className="svcPeopleLbl">{tr("People")}</span>
                            </div>
                          
                            <div className="svcPeopleCtl">
                              <button
                                type="button"
                                className="svcPeopleBtn"
                                onClick={() => setNumPeople((p) => Math.max(1, Number(p || 1) - 1))}
                                aria-label={tr("Decrease people")}
                              >
                                -
                              </button>
                          
                              <input
                                className="svcPeopleInput"
                                type="number"
                                min="1"
                                max="999"
                                value={numPeople}
                                onChange={(e) => setNumPeople(Math.max(1, Number(e.target.value || 1)))}
                                inputMode="numeric"
                              />
                          
                              <button
                                type="button"
                                className="svcPeopleBtn"
                                onClick={() => setNumPeople((p) => Math.max(1, Number(p || 1) + 1))}
                                aria-label={tr("Increase people")}
                              >
                                +
                              </button>
                            </div>
                          </div>        
                      
                          {/* Row 2: Fullname */}
                          {(() => {
                            const ln = modalTable?.reservation?.client_lastname || "";
                            const fn = modalTable?.reservation?.client_firstname || "";
                            const full = `${ln} ${fn}`.trim();
                            return full ? (
                              <div className="svcRow svcNameRow">
                                <b className="svcFullName">{full}</b>
                              </div>
                            ) : null;
                          })()}
                      
                          {/* Row 3: State dropdown + next-state */}
                          <div className="svcRow svcRowState">
                            <select
                              className="svcStateSelect"
                              style={{ borderColor: (getState(selectedState)?.color || "transparent") }}
                              value={selectedState}
                              onChange={(e) => setSelectedState(Number(e.target.value))}
                            >
                              {orderedStates.map((state) => (
                                <option key={state.id} value={state.id}>{state.name}</option>
                              ))}
                            </select>
                      
                            <button
                              type="button"
                              className="svcNextBtn"
                              onClick={() => {
                                if (nextState?.id != null) setSelectedState(Number(nextState.id));
                                else if (orderedStates[0]?.id != null) setSelectedState(Number(orderedStates[0].id));
                              }}
                            >
                              {tr(">>")} {nextState?.name || orderedStates[0]?.name || ""}
                            </button>
                          </div>
                      
                          {/* Row 4: Comment */}
                          <div className="svcRow svcRowComment">
                            <input
                              className="svcCommentInput"
                              type="text"
                              value={comment}
                              onChange={(e) => setComment(e.target.value)}
                              placeholder={tr("Comment")}
                            />
                          </div>
                      
                          {/* Row 5: Save */}
                          <div className="svcRow svcRowSave">
                            <button
                              className="btn-save svcSaveBtn"
                              onClick={handleSaveReservation}
                              disabled={!isDirty}
                              title={isDirty ? "Save changes" : "No changes to save"}
                            >
                              {tr("Save")}
                              {saveTick && <span className="save-check">{"\u2713"}</span>}
                            </button>
                          </div>
                      
                        </div>
                      
                        <div className="reservation-events-container">
                          <ActionsList
                            actions={actions.filter((a) => a.reservation_id === modalTable.reserved_id)}
                            setRefresh={setRefresh}
                            isModal={true}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  {activeTab === "meats" && <AssignMeats reservation={modalTable.reservation} onCloseModal={() => setModalTable(false)} onBeforeSave={async () => {await handleSaveReservation();}} />}
                  {activeTab === "changeTables" && (
                    <div className="svc-changeTables">
                      <div className="svc-changeTables-actions svc-changeTables-actions--top">
                        <button
                          className="svc-primaryBtn"
                          onClick={handleSaveAssignedTables}
                          disabled={!selectedTables || !selectedTables.length}
                        >
                          {tr('Update Assigned Tables')}
                        </button>
                      </div>
                  
                      <div className="svc-changeTables-selector">
                        <TableSelector
                          service_view={true}
                          reservationTime={changeTablesTime}
                          selectedAssignedTables={tables
                            .filter(t => t.reserved_id === modalTable.reserved_id)
                            .map(t => t.id)}
                          activeReservationId={modalTable.reserved_id}
                          onTablesSelected={(newSelection) => setSelectedTables(newSelection)}
                          people={numPeople}
                        />
                      </div>
                  
                      <div className="svc-changeTables-actions">
                        <button
                          className="svc-primaryBtn"
                          onClick={handleSaveAssignedTables}
                          disabled={!selectedTables || !selectedTables.length}
                        >
                          {tr('Update Assigned Tables')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}

export default Service;
